// The product WORDMARK — the app's logo, ONE image in ONE place: the round
// mint disc with "3.5" on it (`public/logo_round.png`, the user's file
// 2026-09-28, cropped tight to the disc — 664px square — so the favicon and
// the mark both fill their box). The same file is the tab icon and the
// manifest icon (public/index.html, public/manifest.json), so the app and
// the browser chrome always show the same mark. Served from `public/`, not
// bundled, on purpose: one path for the page and the HTML head alike.
//
// Since the 2026-09-11 redesign the mark heads the HOME HEADER
// (pages/Leaderboard — wordmark · Digest | Lists · the Lab button) and the
// login wall's masthead (pages/Login). Until 2026-09-28 it was TYPESET —
// "3.5" in Calibri Light / Lato Light, hand-kerned (`MarkGlyphs`), the
// `--font-logo` and `--logo-ink` tokens and the rule drawn under it — all of
// which went with the image: a drawn disc is its own ink and its own rule.
//
// 3.5, from the user's reference screen (2026-09-11); 3.25 until then. The
// version still bumps here (document titles read the literal), but the
// image carries the numerals — a bump means a new file too.
//
// `brand` (2026-09-29, user call: "when starbucks is the brand that logged
// in, instead of the 3.5 logo in the header I want the starbucks logo";
// every workspace since 2026-10-05): the workspace's `{slug, name}`. The
// brand's own mark takes the same box as a round button — its symbol, or
// its initials when all it has is a long wordmark (assets/logos
// `headerMark` decides; `.lab-brand-img.own` / `.lab-brand-puck`,
// index.css) — in the brand's own colours where it has a HEADER_INKS pair
// (tiffany, 2026-10-06), black / white otherwise. No brand at all (the
// wall, the plain board) draws the 3.5 disc. The tab icon stays the
// product's.
import { forwardRef } from "react";

import { headerMark } from "assets/logos";
import { bstyle } from "config/vocab";

export const APP_VERSION = "3.5";

// `process.env.PUBLIC_URL` is CRA's base for `public/` — "" in dev and
// wherever the app is served from the root, the homepage path otherwise.
export const LOGO_SRC = `${process.env.PUBLIC_URL || ""}/logo_round.png`;

// `as`: the element the mark renders as — a plain span by default, a button
// where the header makes it a control (the home's mark returns the reader
// to the digest; components/HomeMark adds the double-click workspace
// menu, which is why the ref is forwarded — the menu anchors to the mark).
// Extra props (onClick, aria-*) pass through. The image's alt is the
// version, so a bare mark still names itself to a screen reader; a host
// with its own `aria-label` (the header button, the wall) overrides it.
const Wordmark = forwardRef(function Wordmark(
  { as: Tag = "span", className = "", brand, ...rest },
  ref,
) {
  const own = headerMark(brand?.slug);
  const label = brand?.name || brand?.slug;
  const size = own?.size ? ` ${own.size}` : "";
  return (
    <Tag
      className={`lab-brand-mark${className ? ` ${className}` : ""}`}
      ref={ref}
      {...rest}
    >
      {!own ? (
        <img
          className="lab-brand-img"
          src={LOGO_SRC}
          alt={APP_VERSION}
          draggable={false}
        />
      ) : own.kind === "disc" ? (
        // a disc mark is painted through a mask on the dusk and needs its
        // file there too — the asset's own URL, as in components/LogoTile
        <img
          className="lab-brand-img own disc"
          src={own.src}
          style={{ "--mark-src": `url(${own.src})` }}
          alt={label}
          draggable={false}
        />
      ) : (
        // every other workspace: a round puck with the mark at its optical
        // tier, or the initials — named once, by the puck. A brand with its
        // own colour pair (assets/logos HEADER_INKS) hands it to the
        // stylesheet as the puck's two variables; the rest fall back to the
        // black / white pairing there.
        <span
          className={`lab-brand-puck${own.ink ? " inked" : ""}`}
          role="img"
          aria-label={label}
          style={
            own.ink
              ? { "--puck-bg": own.ink.bg, "--puck-ink": own.ink.ink }
              : undefined
          }
        >
          {own.kind === "vector" ? (
            <own.Mark
              className={`logo-mark${size}`}
              aria-hidden="true"
              focusable="false"
            />
          ) : own.kind === "knockout" ? (
            // the mark is the HOLE in the file: the mask paints it in the
            // puck's ink, and needs the asset's URL for that
            <span
              className="puck-knock"
              style={{ "--mark-src": `url(${own.src})` }}
            />
          ) : own.kind === "image" ? (
            <img
              className={`logo-mark puck-img${size}`}
              src={own.src}
              alt=""
              draggable={false}
            />
          ) : (
            bstyle(brand.slug).mono
          )}
        </span>
      )}
    </Tag>
  );
});

export default Wordmark;
